import { useLooseProductContextSelector } from 'src/components/restructure/product/contexts/product-context'
import type { Variation } from 'src/components/product-page/Selector/types'
import { useRef } from 'react'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import type { Product } from 'src/components/restructure/product/types/product'
import type { ProductItem } from 'src/components/restructure/product/types/productItem'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import { getInitialItem } from 'src/components/restructure/utils/get-initial-sku'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'

import CustomSelect from './CustomSelect'

interface Props {
  variation: Variation
  onClickVariation: (name: string, value: string) => void
  origin?: 'floating-bar' | 'sidebar'
  hasDisabledButtons?: boolean
  productFromProps?: Product
  currentSkuFromProps?: ProductItem
}

export function DropdownSelector({
  variation,
  onClickVariation,
  hasDisabledButtons,
  origin,
  currentSkuFromProps,
  productFromProps,
}: Readonly<Props>) {
  const contextProduct = useLooseProductContextSelector((ctx) => ctx.product)
  const contextCurrentSku = useLooseProductContextSelector(
    (ctx) => ctx.currentSku
  )

  const { pageData } = useGlobalContext()
  const product = productFromProps ?? contextProduct
  const currentSku = currentSkuFromProps ?? contextCurrentSku
  const oldSizeRef = useRef<string | null>(currentSku?.Tamanho?.[0] ?? null)
  const variationName = variation.field.name

  const sortedVariationValues = [...variation.values].sort(
    (a, b) => a.position - b.position
  )

  const standardObject = getStandardObject(pageData)
  const productObject = getProductQueryObject(
    getInitialItem(product?.items, product?.linkText)
  )

  const keywordsHiddenComponent = [
    'ÚNICO',
    'UNICO',
    'Único',
    'ÚNICA',
    'UNICA',
    'Única',
    'U',
    ',',
    '.',
    'B',
    'N/A',
    'UN',
    'variado',
    'Sem sabor',
    'SEM-SABOR',
    '3953',
    '3954',
  ]

  if (
    sortedVariationValues.length <= 1 &&
    keywordsHiddenComponent.includes(sortedVariationValues[0].name)
  ) {
    return null
  }

  const sendAmplitudeEvent = (variationToSelect: string) => {
    if (!standardObject || !productObject) {
      return
    }

    const selectedItem = product.items.find(
      (productItem) =>
        productItem.Tamanho?.[0] === variationToSelect &&
        productItem.isAvailable
    )

    if (!selectedItem || selectedItem === currentSku) {
      return
    }

    dispatchAmplitudeEvent({
      eventName: 'Product Size Selected',
      eventData: {
        ...standardObject,
        'product details': [{ ...productObject, size: variationToSelect }],
        'old size selected': oldSizeRef.current,
        'new size selected': variationToSelect,
      },
    })
    oldSizeRef.current = variationToSelect
  }

  return (
    <div>
      <CustomSelect
        key={variation.field.name}
        onChange={(e) => {
          onClickVariation(variationName, e.name)
          sendAmplitudeEvent(e.name)
        }}
        options={sortedVariationValues}
        auxiliaryText={`${variation.field.name}: `}
        origin={origin}
        hasDisabledButtons={hasDisabledButtons}
      />
    </div>
  )
}
